import React from 'react';
import { CalendarEvent, statusConfig, assetTypeConfig } from './types';
import { formatFinnishCurrency, formatFinnishNumber } from '@/utils';

interface EventCardProps {
  event: CalendarEvent;
  onClick: () => void;
  showStatusLabel?: boolean;
  showAssetTypeLabel?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({
  event,
  onClick,
  showStatusLabel = true,
  showAssetTypeLabel = true,
}) => {
  const config = statusConfig[event.status] || {
    label: event.status || 'Unknown',
    color: 'bg-gray-500',
    lightColor: 'bg-gray-100 dark:bg-gray-900 contrast:bg-gray-900',
    textColor: 'text-gray-800 dark:text-gray-200 contrast:text-gray-100',
    borderColor: 'border-gray-500',
  };
  const isConflict = event.isConflicting;
  const isAvailable = event.isAvailable;
  const assetType = event.assetType || (event.service ? 'service' : 'berth');
  const assetConfig = assetTypeConfig[assetType] || {
    label: assetType || 'Unknown',
    color: 'bg-gray-500',
    lightColor: 'bg-gray-100 dark:bg-gray-900 contrast:bg-gray-900',
    textColor: 'text-gray-800 dark:text-gray-200 contrast:text-gray-100',
    borderColor: 'border-gray-500',
  };
  const isAddon = assetType === 'addon';
  const isService = assetType === 'service';

  // Format price if available
  const formattedPrice = React.useMemo(() => {
    if (event.price === null || event.price === undefined) return '';

    const priceUnit = event.priceUnit || '';
    const unitDisplay =
      priceUnit === 'PER_DAY' ? '/day' : priceUnit === 'PER_HOUR' ? '/hr' : '';

    return `${formatFinnishCurrency(event.price)}${unitDisplay ? ` ${unitDisplay}` : ''}`;
  }, [event.price, event.priceUnit]);

  return (
    <div
      onClick={onClick}
      className={`
        ${config.lightColor} ${config.textColor}
        border rounded-lg p-2 mb-1 cursor-pointer transition-all duration-200 relative
        hover:shadow-md hover:scale-105 transform
        ${isConflict ? 'border-red-600 ring-1 ring-red-400/60 before:absolute before:inset-0 before:bg-[repeating-linear-gradient(45deg,rgba(255,0,0,0.08)_0,rgba(255,0,0,0.08)_6px,transparent_6px,transparent_12px)] before:rounded-lg' : config.borderColor}
        ${isAvailable && config.pattern ? config.pattern : ''}
        ${event.isBlocked && config.pattern ? config.pattern : ''}
        ${isAddon ? 'border-l-4 border-l-purple-500' : isService ? 'border-l-4 border-l-orange-500' : ''}
      `}
    >
      <div className="flex items-start gap-2">
        <span
          className={`w-2 h-2 rounded-full mt-1.5 ${isConflict ? 'bg-red-900 animate-pulse' : config.color} flex-shrink-0`}
        ></span>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-sm truncate">{event.title}</p>
          <div className="text-xs opacity-75 truncate flex items-center gap-1 flex-wrap">
            {showStatusLabel ? <span>{config.label}</span> : null}

            {/* Asset Type Badge */}
            {showAssetTypeLabel ? (
              <span
                className={`inline-block px-1.5 py-0.5 text-[10px] font-medium ${assetConfig.lightColor} ${assetConfig.textColor} rounded`}
                title={`Asset Type: ${assetConfig.label}`}
              >
                {assetConfig.label}
              </span>
            ) : null}

            {/* Status Indicators */}
            {isConflict && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-red-600 text-white rounded">
                Conflict
              </span>
            )}
            {isAvailable && (
              <span className="inline-block px-1.5 py-0.5 text-[10px] font-semibold bg-green-600 contrast:bg-green-800 text-white rounded">
                Available
              </span>
            )}

            {/* Price display for addons and services */}
            {(isAddon || isService) && formattedPrice && (
              <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium ${assetConfig.lightColor} ${assetConfig.textColor} rounded ml-auto`}>
                {formattedPrice}
              </span>
            )}
          </div>

          {/* If addon has extra information */}
          {isAddon && event.addon?.description && (
            <p className="text-[10px] text-gray-600 mt-1 truncate">
              {event.addon.description.substring(0, 30)}
              {event.addon.description.length > 30 ? '...' : ''}
            </p>
          )}

          {/* Quantity display for addons */}
          {isAddon && event.addon?.quantity && event.addon.quantity > 0 && (
            <div className="text-[10px] text-gray-600 contrast:text-gray-900 mt-0.5">
              Quantity:{' '}
              {formatFinnishNumber(event.addon.quantity, {
                maximumFractionDigits: 0,
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default EventCard;
